import React, { useCallback } from 'react';
import styled from 'styled-components';
import type { LandingArchiveCandidate, LandingArchiveGroup } from '../../shared/util/landingArchiveIndex';
import {
  LANDING_COLUMN_GAP2,
  LANDING_COLUMN_WIDTH2,
} from '../../shared/util/exhibitionVenueIndex';

export interface ExhibitionVenueIndexProps {
  columns: LandingArchiveGroup[][];
  activeVenueId: string | null;
  onVenueActivate: (candidate: LandingArchiveCandidate) => void;
}

const ExhibitionVenueIndex: React.FC<ExhibitionVenueIndexProps> = ({
  columns,
  activeVenueId,
  onVenueActivate,
}) => {
  const handleVenueClick = useCallback(
    (e: React.MouseEvent, candidate: LandingArchiveCandidate) => {
      e.stopPropagation();
      onVenueActivate(candidate);
    },
    [onVenueActivate],
  );

  const renderVenueItem = (
    candidate: LandingArchiveCandidate,
    key: string,
  ) => {
    const isActive = activeVenueId === candidate.aui;
    return (
      <VenueItem
        key={key}
        onClick={(e) => handleVenueClick(e, candidate)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onVenueActivate(candidate);
          }
        }}
      >
        <VenueName $isActive={isActive}>{candidate.username}</VenueName>
      </VenueItem>
    );
  };

  return (
    <IndexContainer>
      <ColumnList>
        {columns.map((column, columnIndex) => (
          <Column key={`exhibition-column-${columnIndex}`}>
            {column.map((group) => (
              <GroupSection key={group.letter}>
                {group.label ? <GroupLabel>{group.label}</GroupLabel> : null}
                {group.candidates.map((candidate, candidateIndex) =>
                  renderVenueItem(
                    candidate,
                    `${candidate.aui}-${candidateIndex}`,
                  )
                )}
              </GroupSection>
            ))}
          </Column>
        ))}
      </ColumnList>
    </IndexContainer>
  );
};

const IndexContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: hidden;
`;

const ColumnList = styled.div`
  width: max-content;
  display: flex;
  align-items: flex-start;
  gap: ${LANDING_COLUMN_GAP2};
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: ${LANDING_COLUMN_WIDTH2};
  flex-shrink: 0;
`;

const GroupSection = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  padding-bottom: 8px;
`;

const GroupLabel = styled.div`
  width: 100%;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  line-height: 1;
`;

const VenueItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;

  &:hover > div:first-child {
    transform: scale(0.95);
  }
`;

const VenueName = styled.div<{ $isActive?: boolean }>`
  padding: 3px 0;
  width: clamp(110px, 50%, 180px);
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.color_Gray_02 : theme.colors.color_Gray_03};
  text-decoration: ${({ $isActive }) => ($isActive ? 'underline' : 'none')};
  transform-origin: left center;
  transition: transform 0.25s ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export default ExhibitionVenueIndex;
